import { DataKey, ScatterAndLineData, TrendlineType } from "./ScatterAndLineChart";

export type CalculateTrendsOptions = {
  constrainToZero?: boolean;
  movingAverageRange?: number;
};

function getTrendlineData(data: ScatterAndLineData[]) {
  return data.filter((point) => point.outlier !== true && point.replica !== true);
}

export function calculateTrends(
  data: ScatterAndLineData[],
  dataKey: DataKey,
  type: TrendlineType,
  options: CalculateTrendsOptions = {
    constrainToZero: false,
    movingAverageRange: 0,
  }
) {
  const trendlineData = getTrendlineData(data);

  if (type === "linear-trend") {
    const n = trendlineData.length;
    if (n === 0) return data;

    let sumX = 0;
    let sumY = 0;
    let sumXY = 0;
    let sumX2 = 0;

    trendlineData.forEach((point) => {
      const x = Number(point[dataKey.x]);
      const y = Number(point[dataKey.y]);
      sumX += x;
      sumY += y;
      sumXY += x * y;
      sumX2 += x * x;
    });

    const denom = n * sumX2 - sumX * sumX;
    const slope = denom === 0 ? 0 : (n * sumXY - sumX * sumY) / denom;
    const intercept = (sumY - slope * sumX) / n;

    return data.map((point) => {
      const value = slope * Number(point[dataKey.x]) + intercept;
      return {
        ...point,
        [`linear-trend_${dataKey.y}`]: options.constrainToZero ? Math.max(0, value) : value,
      };
    });
  } else if (type === "moving-average") {
    // If range is not provided, fallback to previous behavior (group by x)
    if (!options.movingAverageRange) {
      // compute average y for each unique x, and attach that avg to every point that has that x
      const groups = new Map<any, { sum: number; count: number }>();

      trendlineData.forEach((point) => {
        const x = point[dataKey.x];
        const y = Number(point[dataKey.y]);
        const entry = groups.get(x);
        if (entry) {
          entry.sum += y;
          entry.count += 1;
        } else {
          groups.set(x, { sum: y, count: 1 });
        }
      });

      const averages = new Map<any, number>();
      groups.forEach((val, key) => {
        averages.set(key, val.count === 0 ? 0 : val.sum / val.count);
      });

      return data.map((point) => ({
        ...point,
        [`moving-average_${dataKey.y}`]: averages.get(point[dataKey.x]) ?? null,
      }));
    }
    // Calculate moving average using a range
    return data.map((point) => {
      const x = Number(point[dataKey.x]);
      // Find all points within the range
      const inRange = trendlineData.filter((p) => {
        const px = Number(p[dataKey.x]);
        return Math.abs(px - x) <= (options.movingAverageRange || 0);
      });

      const sum = inRange.reduce((acc, p) => acc + Number(p[dataKey.y]), 0);
      const avg = inRange.length === 0 ? null : sum / inRange.length;
      return {
        ...point,
        [`moving-average_${dataKey.y}`]: avg,
      };
    });
  }
  return data;
}
